import { Badge, Spinner, Text, Tooltip, makeStyles, tokens } from '@fluentui/react-components';
import type { PhotoMissingReason } from '@worklogix/shared';
import { useEffect, useRef, useState } from 'react';

import { SideBlade } from '../components/SideBlade';

import type { PhotoBadgeKind } from './photoUrl';
import { missingReasonText } from './photoUrl';
import { usePunchPhoto } from './usePunchPhoto';

const useStyles = makeStyles({
  thumbButton: {
    padding: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    lineHeight: 0,
    borderRadius: tokens.borderRadiusMedium,
  },
  thumb: {
    width: '40px',
    height: '40px',
    objectFit: 'cover',
    borderRadius: tokens.borderRadiusMedium,
    display: 'block',
  },
  placeholder: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase100,
    textAlign: 'center',
  },
  enlarged: {
    maxWidth: '100%',
    maxHeight: '70vh',
    objectFit: 'contain',
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  enlargedWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    alignItems: 'center',
  },
});

/** ARIA/alt copy that carries no PII — never the employee name. */
const PHOTO_ALT = 'Punch verification photo';

interface PunchThumbnailProps {
  /** Punch whose photo to show; the RLS proxy is addressed by this id. */
  punchId: string;
  /** Which boundary this thumbnail is (open/close) — for the enlarge title + a11y. */
  role: 'open' | 'close';
  /** Enlarge request (click / Enter). */
  onEnlarge: (punchId: string, role: 'open' | 'close') => void;
}

/**
 * A small, lazily-loaded punch-photo thumbnail. The image fetch is deferred by an
 * `IntersectionObserver` until the thumbnail scrolls into view, so a long session
 * list does not issue an authenticated request per row up front. On any failure
 * (missing photo, cross-tenant 404, transient error) it collapses to a subtle
 * "No photo" placeholder — the authoritative missing/required badge is rendered
 * from metadata in the blade.
 */
export function PunchThumbnail({ punchId, role, onEnlarge }: PunchThumbnailProps): JSX.Element {
  const styles = useStyles();
  const containerRef = useRef<HTMLSpanElement | null>(null);
  const [visible, setVisible] = useState(false);
  const { state, url } = usePunchPhoto(punchId, visible);

  useEffect(() => {
    const node = containerRef.current;
    if (node === null || visible) {
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          setVisible(true);
        }
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [visible]);

  if (state === 'error') {
    return (
      <span ref={containerRef} className={styles.placeholder} aria-label="No photo">
        No photo
      </span>
    );
  }

  if (state !== 'ready' || url === undefined) {
    return (
      <span ref={containerRef} className={styles.placeholder} aria-hidden="true">
        {visible ? <Spinner size="tiny" /> : null}
      </span>
    );
  }

  return (
    <span ref={containerRef}>
      <button
        type="button"
        className={styles.thumbButton}
        aria-label={`Enlarge ${role} punch photo`}
        onClick={(e) => {
          e.stopPropagation();
          onEnlarge(punchId, role);
        }}
      >
        <img className={styles.thumb} src={url} alt={PHOTO_ALT} loading="lazy" />
      </button>
    </span>
  );
}

interface MissingPhotoBadgeProps {
  /** Semantic badge state from {@link photoBadgeKind}; `none` renders nothing. */
  kind: PhotoBadgeKind;
  /** Non-PII reason enum, mapped to friendly tooltip copy. */
  reason: PhotoMissingReason | undefined;
}

/**
 * "No photo" placeholder badge, reusing the flags.ts badge idiom (filled Fluent
 * `Badge` + `Tooltip`). Escalates to a warning colour when the tenant policy is
 * (or is assumed) `required` — sits alongside the existing needs-review badge.
 */
export function MissingPhotoBadge({ kind, reason }: MissingPhotoBadgeProps): JSX.Element | null {
  if (kind === 'none') {
    return null;
  }
  const isRequired = kind === 'missing-required';
  return (
    <Tooltip content={missingReasonText(reason)} relationship="label">
      <Badge appearance="filled" color={isRequired ? 'warning' : 'informative'}>
        {isRequired ? 'Photo required — missing' : 'No photo'}
      </Badge>
    </Tooltip>
  );
}

interface PhotoEnlargeDialogProps {
  /** Punch to enlarge, or null when closed. */
  punchId: string | null;
  /** Role of the enlarged photo (for the title). */
  role: 'open' | 'close' | null;
  onClose: () => void;
}

/**
 * Click-to-enlarge surface. Reuses the existing {@link SideBlade} idiom (rather
 * than introducing a new modal pattern) to show the full-resolution punch photo
 * off the SAME RLS proxy. Re-fetches on open; nothing is cached.
 */
export function PhotoEnlargeDialog({
  punchId,
  role,
  onClose,
}: PhotoEnlargeDialogProps): JSX.Element {
  const styles = useStyles();
  const open = punchId !== null;
  const { state, url } = usePunchPhoto(punchId ?? undefined, open);
  const title =
    role === null ? 'Punch photo' : `${role === 'open' ? 'Clock-in' : 'Clock-out'} photo`;

  return (
    <SideBlade open={open} onClose={onClose} title={title} size="large">
      <div className={styles.enlargedWrap}>
        {state === 'ready' && url !== undefined ? (
          <img className={styles.enlarged} src={url} alt={PHOTO_ALT} />
        ) : state === 'error' ? (
          <Text>The photo could not be loaded.</Text>
        ) : (
          <Spinner label="Loading photo…" />
        )}
      </div>
    </SideBlade>
  );
}
